import { useEffect, useState } from "react";
import { Mail, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";
import Lightbox from "./Lightbox";

// sessionStorage key for dismissal state. The stored value is the active
// lightbox type + version timestamp (e.g. "email::2026-04-27T...") — purely a
// UX flag so we don't re-show a dismissed campaign in the same tab session.
// No personal or sensitive data is ever stored here.
const SESSION_KEY = "lb_dismissed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

const getDismissedKey = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) || "";
  } catch (err) {
    // sessionStorage can be disabled (private mode, security policies). We
    // treat that as "never dismissed" — the lightbox will always show, which
    // is acceptable for a non-critical UX flag.
    logger.warn("sessionStorage read blocked:", err.message);
    return "";
  }
};

const setDismissedKey = (key) => {
  try {
    sessionStorage.setItem(SESSION_KEY, key);
  } catch (err) {
    logger.warn("sessionStorage write blocked:", err.message);
  }
};

export default function HomeLightboxes() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const cfg = await api.getLightboxConfig();
        if (!cfg || cfg.active === "off") return;

        // Per-version dismissal: re-show the lightbox after admin updates it
        const versionKey = `${cfg.active}::${cfg.version || ""}`;
        if (getDismissedKey() === versionKey) return;

        setConfig(cfg);
        setOpen(true);
      } catch (err) {
        // Lightbox is non-critical — never block the home page on its failure
        logger.warn("Lightbox config fetch failed:", err.message);
      }
    };
    load();
  }, []);

  const dismiss = () => {
    if (config) {
      setDismissedKey(`${config.active}::${config.version || ""}`);
    }
    setOpen(false);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.subscribe(email.trim());
      setSubmitted(true);
      // Auto-dismiss success state after 2s
      setTimeout(() => {
        dismiss();
      }, res.already_subscribed ? 2000 : 2500);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Could not subscribe right now. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !config) return null;

  // ---- Email lightbox ----
  if (config.active === "email") {
    return (
      <Lightbox open={open} onClose={dismiss} testId="email-lightbox">
        <div className="p-8 sm:p-10" data-testid="email-lightbox-body">
          {submitted ? (
            <div className="text-center py-6" data-testid="email-lightbox-success">
              <CheckCircle2 className="w-14 h-14 text-demolition-red mx-auto mb-4" />
              <h2 className="font-russo text-3xl text-white uppercase tracking-tighter mb-2">
                YOU&apos;RE IN
              </h2>
              <p className="font-mono text-white/70 text-sm">
                Watch your inbox for the next DemoPod drop.
              </p>
            </div>
          ) : (
            <>
              <Mail className="w-12 h-12 text-demolition-red mb-4" />
              <h2 className="font-russo text-3xl sm:text-4xl text-white uppercase tracking-tighter mb-3">
                {config.email_headline || "Stay in the loop"}
              </h2>
              <p className="font-mono text-white/70 text-sm mb-6 leading-relaxed">
                {config.email_description ||
                  "Get notified about new DemoPod episodes and merch drops. No spam, ever."}
              </p>

              <form onSubmit={handleSubscribe} className="space-y-4" data-testid="email-lightbox-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoFocus
                  className="w-full px-4 py-3 bg-demolition-subtle border border-white/20 text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-demolition-red transition-colors"
                  data-testid="email-lightbox-input"
                />
                {error && (
                  <p className="font-mono text-demolition-red text-xs" data-testid="email-lightbox-error">
                    {error}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-6 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-widest hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
                    data-testid="email-lightbox-submit"
                  >
                    {submitting ? "SUBSCRIBING..." : "SUBSCRIBE"}
                  </button>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="px-6 py-3 border border-white/20 text-white/60 font-teko text-lg uppercase tracking-widest hover:bg-white/5 hover:text-white transition-colors"
                    data-testid="email-lightbox-decline"
                  >
                    No thanks
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </Lightbox>
    );
  }

  // ---- Promo lightbox ----
  if (config.active === "promo") {
    const promo = config.promo || {};
    const hasLink = !!(promo.link_url && promo.link_url.trim());
    return (
      <Lightbox open={open} onClose={dismiss} testId="promo-lightbox">
        {promo.image_url && (
          <div className="aspect-video bg-demolition-subtle overflow-hidden border-b border-white/10">
            <img
              src={resolveImage(promo.image_url)}
              alt={promo.headline || "Promotion"}
              className="w-full h-full object-cover"
              data-testid="promo-lightbox-image"
            />
          </div>
        )}
        <div className="p-8 sm:p-10" data-testid="promo-lightbox-body">
          {promo.headline && (
            <h2
              className="font-russo text-3xl sm:text-4xl text-white uppercase tracking-tighter mb-3"
              data-testid="promo-lightbox-headline"
            >
              {promo.headline}
            </h2>
          )}
          {promo.description && (
            <p
              className="font-mono text-white/70 text-sm mb-6 leading-relaxed whitespace-pre-line"
              data-testid="promo-lightbox-description"
            >
              {promo.description}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {hasLink && (
              <a
                href={promo.link_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismiss}
                className="flex-1 px-6 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-widest text-center hover:bg-demolition-red-hover transition-colors"
                data-testid="promo-lightbox-cta"
              >
                Learn more
              </a>
            )}
            <button
              type="button"
              onClick={dismiss}
              className={`${hasLink ? "" : "flex-1"} px-6 py-3 border border-white/20 text-white/60 font-teko text-lg uppercase tracking-widest hover:bg-white/5 hover:text-white transition-colors`}
              data-testid="promo-lightbox-dismiss"
            >
              Close
            </button>
          </div>
        </div>
      </Lightbox>
    );
  }

  return null;
}
